import React, {useEffect, useRef, useState} from 'react';
import {Platform, StyleSheet} from 'react-native';
import {SafeAreaProvider} from 'react-native-safe-area-view';
import {useIsDesktop} from '../../hooks/useIsDesktop';
import {newGuid} from '../../pipes/guid';
import {useTheme} from '../../theme';
import {Theme} from '../../theme/interface';

interface Modal {
  id: string;
  render(params: Modal, methods: ModalMethods): React.ReactNode;
  mobile?: boolean;
  allowBodyScroll?: boolean;
  hideCloseButton?: boolean;
  hasOverlay?: boolean;
}

interface ModalMethods {
  clear(): void;
  pop(): void;
  remove(id: string): void;
  show(modal: Omit<Modal, 'id'>): string;
  getLast(): Modal | null;
}

interface ModalControllerProps {
  modalStack: Modal[];
  setModalStack: React.Dispatch<React.SetStateAction<Modal[]>>;
  isDesktop: boolean;
  theme: Theme;
}

class ModalController extends React.PureComponent<ModalControllerProps> implements ModalMethods {
  clear() {
    this.props.setModalStack([]);
  }
  pop() {
    const last = this.props.modalStack[this.props.modalStack.length - 1];
    if (last) {
      this.props.setModalStack(this.props.modalStack.filter((m) => m.id !== last.id));
    }
  }
  remove(id: string) {
    const candidate = this.props.modalStack.find((m) => m.id === id);
    if (candidate) {
      this.props.setModalStack(this.props.modalStack.filter((m) => m.id !== candidate.id));
    }
  }
  show(modal: Omit<Modal, 'id'>) {
    const id = newGuid();
    this.props.setModalStack([...this.props.modalStack, {id, ...modal}]);
    return id;
  }
  getLast(): Modal | null {
    const displayedModalStack = this.props.modalStack.filter((item) => (!this.props.isDesktop ? !!item.mobile : true));
    if (displayedModalStack.length === 0) {
      return null;
    }

    return displayedModalStack[displayedModalStack.length - 1];
  }

  render = () => {
    const {theme} = this.props;
    const activeModal = this.getLast();
    const backgroundColor = activeModal?.hasOverlay ? theme.color.grayMenu : undefined;

    return (
      <ModalContext.Provider value={this}>
        {this.props.children}
        {activeModal && (
          <SafeAreaProvider style={[StyleSheet.absoluteFill, {backgroundColor}]}>
            {activeModal.render(activeModal, this)}
          </SafeAreaProvider>
        )}
      </ModalContext.Provider>
    );
  };
}

const ModalContext = React.createContext<ModalMethods | undefined>(undefined);

export const ModalProvider: React.FC = ({children}) => {
  const [modalStack, setModalStack] = useState<Modal[]>([]);
  const isDesktop = useIsDesktop();
  const controller = useRef<ModalController>(null);
  const theme = useTheme();

  if (Platform.OS === 'web') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (!controller.current) {
        return;
      }
      if (controller.current.getLast()?.allowBodyScroll) {
        return;
      }
      const body = document.querySelector('body');
      if (body) {
        if (modalStack.length > 0) {
          body.classList.add('modal-open');
        } else {
          body.classList.remove('modal-open');
        }
      }

      return () => {
        if (body) {
          body.classList.remove('modal-open');
        }
      };
    }, [modalStack, controller.current]);
  }

  useEffect(() => {
    if (!controller.current) {
      return;
    }
    if (!isDesktop) {
      controller.current.clear();
    }
  }, [isDesktop, controller.current]);

  return (
    <ModalController
      modalStack={modalStack}
      setModalStack={setModalStack}
      isDesktop={isDesktop}
      ref={controller}
      theme={theme}>
      {children}
    </ModalController>
  );
};

export const useModal = () => React.useContext(ModalContext) as ModalMethods;
